let activePicker = null;

const numericAttribute = (element, name) => {
  const value = element[name]?.baseVal?.value;

  return Number.isFinite(value) ? value : Number(element.getAttribute(name) || 0);
};

const svgPointFromEvent = (svg, event) => {
  const rect = svg.getBoundingClientRect();
  const viewBox = svg.viewBox.baseVal;

  if (!rect.width || !rect.height || !viewBox.width || !viewBox.height) {
    return null;
  }

  return {
    x: viewBox.x + ((event.clientX - rect.left) / rect.width) * viewBox.width,
    y: viewBox.y + ((event.clientY - rect.top) / rect.height) * viewBox.height,
  };
};

const imagePointFromSvgPoint = (source, image, point) => {
  const x = numericAttribute(source, 'x');
  const y = numericAttribute(source, 'y');
  const width = numericAttribute(source, 'width');
  const height = numericAttribute(source, 'height');

  if (!width || !height || !image.naturalWidth || !image.naturalHeight) {
    return null;
  }

  const preserveAspectRatio = source.getAttribute('preserveAspectRatio') || 'xMidYMid meet';

  if (preserveAspectRatio.includes('none')) {
    const imageX = ((point.x - x) / width) * image.naturalWidth;
    const imageY = ((point.y - y) / height) * image.naturalHeight;

    return imageX < 0 || imageY < 0 || imageX >= image.naturalWidth || imageY >= image.naturalHeight
      ? null
      : { x: imageX, y: imageY };
  }

  const scale = preserveAspectRatio.includes('slice')
    ? Math.max(width / image.naturalWidth, height / image.naturalHeight)
    : Math.min(width / image.naturalWidth, height / image.naturalHeight);
  const renderedWidth = image.naturalWidth * scale;
  const renderedHeight = image.naturalHeight * scale;
  const alignX = preserveAspectRatio.includes('xMin') ? 0 : preserveAspectRatio.includes('xMax') ? width - renderedWidth : (width - renderedWidth) / 2;
  const alignY = preserveAspectRatio.includes('YMin') ? 0 : preserveAspectRatio.includes('YMax') ? height - renderedHeight : (height - renderedHeight) / 2;
  const imageX = (point.x - x - alignX) / scale;
  const imageY = (point.y - y - alignY) / scale;

  return imageX < 0 || imageY < 0 || imageX >= image.naturalWidth || imageY >= image.naturalHeight
    ? null
    : { x: imageX, y: imageY };
};

/**
 * Enables a one-time eyedropper on the current SVG preview. It is only called
 * by colour controls that explicitly opt in through their template config.
 */
export const startSvgColorPicker = (picker, onColor) => {
  activePicker?.();

  let cancelled = false;
  let cleanup = () => {};
  const cancel = () => {
    cancelled = true;
    cleanup();

    if (activePicker === cancel) {
      activePicker = null;
    }
  };

  activePicker = cancel;

  const sourceId = picker?.sourceId || picker?.source;
  const svg = document.querySelector('.template svg');
  const source = sourceId ? svg?.querySelector(`#${sourceId}`) : null;
  const href = source?.getAttribute('href') || source?.getAttributeNS('http://www.w3.org/1999/xlink', 'href');

  if (!svg || !source || !href) {
    window.alert('Für die Farbauswahl ist zunächst ein Hintergrundbild erforderlich.');
    cancel();
    return;
  }

  const image = new Image();
  image.crossOrigin = 'anonymous';

  image.onload = () => {
    if (cancelled) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });

    try {
      context.drawImage(image, 0, 0);
    } catch (error) {
      window.alert('Das Hintergrundbild kann nicht für die Farbauswahl gelesen werden.');
      cancel();
      return;
    }

    const previousCursor = svg.style.cursor;
    svg.style.cursor = 'crosshair';

    const sample = (event) => {
      const svgPoint = svgPointFromEvent(svg, event);
      const imagePoint = svgPoint && imagePointFromSvgPoint(source, image, svgPoint);

      if (!imagePoint) {
        return false;
      }

      try {
        const [red, green, blue] = context.getImageData(Math.floor(imagePoint.x), Math.floor(imagePoint.y), 1, 1).data;
        onColor(`${red},${green},${blue}`);
        return true;
      } catch (error) {
        window.alert('Das Hintergrundbild kann nicht für die Farbauswahl gelesen werden.');
        cancel();
        return false;
      }
    };

    const move = (event) => sample(event);
    const select = (event) => {
      if (sample(event)) {
        cancel();
      }
    };
    const keydown = (event) => {
      if (event.key === 'Escape') {
        cancel();
      }
    };

    cleanup = () => {
      svg.style.cursor = previousCursor;
      svg.removeEventListener('pointermove', move);
      svg.removeEventListener('pointerdown', select);
      window.removeEventListener('keydown', keydown);
    };

    svg.addEventListener('pointermove', move);
    svg.addEventListener('pointerdown', select);
    window.addEventListener('keydown', keydown);
  };

  image.onerror = () => {
    if (!cancelled) {
      window.alert('Das Hintergrundbild kann nicht für die Farbauswahl geladen werden.');
      cancel();
    }
  };

  image.src = href;
};
